import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './components/Home';
import About from './components/About';
import Services from './components/Services';
import ContactUs from './components/ContactUs';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import './App.css';
import TitleUpdater from './components/TitleUpdater';
import ScrollToTop from './components/ScrollToTop';
import { HelmetProvider } from 'react-helmet-async';
import { checkUserStatus } from './services/api';
import KYCPage from './components/KYCPage';
import NotFound from './components/NotFound';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing user session on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('cwUser');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (err) {
        localStorage.removeItem('cwUser');
      }
    }
    setLoading(false);
  }, []);

  // Periodic status check for blocked users and monthly validation
  useEffect(() => {
    if (!user) return;

    // Monthly Validation (10th of every month)
    const today = new Date();
    if (today.getDate() === 10) {
      const currentMonthYear = `${today.getMonth() + 1}-${today.getFullYear()}`;
      const lastValidation = localStorage.getItem('monthlyValidation');

      if (lastValidation !== currentMonthYear) {
        alert('Monthly security validation required. Please log in again.');
        localStorage.removeItem('cwUser');
        localStorage.setItem('monthlyValidation', currentMonthYear);
        setUser(null);
        return;
      }
    }

    const performCheck = async () => {
      try {
        const response = await checkUserStatus(user.buyerId);
        if (response && response.success && response.data && response.data.status) {
          if (response.data.status.toLowerCase() === 'blocked') {
            alert('Your account has been blocked by the administrator. You will be logged out.');
            localStorage.removeItem('cwUser');
            setUser(null);
            // Navigation handled by state change and ProtectedRoute
          }
        }
      } catch (error) {
        console.error('Status check failed', error);
      }
    };

    // Initial check
    performCheck();

    // Interval check every 30 seconds
    const interval = setInterval(() => {
      // Re-check monthly validation in interval to catch date change
      const checkToday = new Date();
      if (checkToday.getDate() === 10) {
        const checkMonthYear = `${checkToday.getMonth() + 1}-${checkToday.getFullYear()}`;
        const checkLastValidation = localStorage.getItem('monthlyValidation');
        if (checkLastValidation !== checkMonthYear) {
          alert('Monthly security validation required. Please log in again.');
          localStorage.removeItem('cwUser');
          localStorage.setItem('monthlyValidation', checkMonthYear);
          setUser(null);
          return;
        }
      }
      performCheck();
    }, 30000);

    return () => clearInterval(interval);
  }, [user]);

  // Protected Route Component
  const ProtectedRoute = ({ children }) => {
    if (loading) {
      return <div className="loading-screen">Loading...</div>;
    }
    return user ? children : <Navigate to="/login" />;
  };

  // Public Route Component (redirect to dashboard if logged in)
  const PublicRoute = ({ children }) => {
    if (loading) {
      return <div className="loading-screen">Loading...</div>;
    }
    return !user ? children : <Navigate to="/dashboard" />;
  };

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
      <TitleUpdater />
      <div className="App">
        <Routes>
          {/* Public Pages */}
          <Route path="/" element={<Home user={user} setUser={setUser} />} />
          <Route path="/about" element={<About user={user} />} />
          <Route path="/services" element={<Services user={user} />} />
          <Route path="/contact" element={<ContactUs user={user} />} />
          <Route path="/kyc-verification" element={<KYCPage />} />


          {/* Auth Pages */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login setUser={setUser} />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />

          {/* Protected Pages */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />

          {/* Default Route */}
          <Route path="*" element={<NotFound user={user} setUser={setUser} />} />
        </Routes>
      </div>
      <Analytics />
      <SpeedInsights />
    </Router>
    </HelmetProvider>
  );
}

export default App;
